import { Card, Col } from 'antd';
import { head } from 'lodash/fp';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { type MobilityStockPublicListFragment } from '../../../../api/fragments/MobilityStockPublicList';
import useCompanyFormats from '../../../../utilities/useCompanyFormats';
import usePublic from '../../../../utilities/usePublic';
import { type TranslatedString } from '../../../../utilities/useTranslatedString';
import { StyledVehicleCardMeta } from '../../../shared/CIPage/carListingUI';

const StyledStockCard = styled(Card)<{ isSelected: boolean; onClick?: () => void; disabled?: boolean }>`
    max-width: 400px;
    margin: 0 auto;
    border: unset;

    margin-bottom: 15px;
    & img {
        height: auto;
        width: 100%;
        aspect-ratio: 16/9;
        object-fit: cover;
        border-radius: var(--vehicle-image-border-radius);
    }

    ${({ onClick, disabled }) =>
        onClick &&
        css`
            cursor: ${disabled ? 'not-allowed' : 'pointer'};
        `}

    & .ant-card-body {
        padding: 0;
        margin-top: 1em;
    }

    & .ant-card-meta-detail > div:not(:last-child) {
        font-weight: bold;
        margin-bottom: 0;
    }
`;

const ImageContainer = styled.div<{ isSelected: boolean }>`
    height: auto;
    width: 100%;
    aspect-ratio: 16/9;
    object-fit: cover;
    border-radius: var(--image-selection-border-radius);
    border: var(--image-selection-border);
    outline: var(--image-selection-outline);
    outline-offset: var(--image-selection-outline-offset);

    ${({ isSelected }) =>
        isSelected &&
        css`
            border-color: var(--image-selection-active-border-color);
            outline-color: var(--image-selection-active-outline-color);
        `}
`;

type StockCarCardProps = {
    stock: MobilityStockPublicListFragment;
    translated: TranslatedString;
    selectedStockId: string;
    setSelectedStockId: (stockId: string) => void;
    span: number;
};

const StockCarCard = ({ stock, translated, selectedStockId, setSelectedStockId, span }: StockCarCardProps) => {
    const fallbackSrc = usePublic('empty.svg');
    const { t } = useTranslation('giftVoucherJourney');

    const onClick = useCallback(() => {
        setSelectedStockId(stock.id);
    }, [setSelectedStockId, stock.id]);

    const { formatAmountWithCurrency } = useCompanyFormats();

    if (stock.__typename === 'ConfiguratorStockInventory' || stock.inventory.__typename === 'ConfiguratorInventory') {
        return null;
    }

    const image = head(stock.images)?.url || head(stock.inventory.variant.images)?.url || fallbackSrc;
    const stockCarPrice = stock.price || stock.inventory.variant.vehiclePrice;

    return (
        <Col span={span}>
            <StyledStockCard
                key={stock.id}
                cover={
                    <ImageContainer isSelected={selectedStockId === stock.id}>
                        <img alt={stock.id} src={image} />
                    </ImageContainer>
                }
                isSelected={selectedStockId === stock.id}
                onClick={onClick}
            >
                <StyledVehicleCardMeta
                    description={t('giftVoucherJourney:vehicle.card.description', {
                        stockPriceWithCurrency: formatAmountWithCurrency(stockCarPrice),
                    })}
                />
            </StyledStockCard>
        </Col>
    );
};

export default StockCarCard;
